<template>
  <div class="flex flex-col lg:flex-row items-start">
    <!-- Suggested Lists -->

    <div v-if="!show_form" class="w-full rounded-md px-5 mt-5">
      <div class="flex">
        <div class="w-2/3">
          <h2 class="text-base font-medium text-light-black-text dark:text-dark-title-text pb-4">
            {{ $t("Saved Lists") }}
          </h2>

          <div class="grid grid-cols-2 gap-2">
            <div
              v-for="list in suggested_lists"
              :key="list.id"
              class="col-span-1 bg-white dark:bg-dark-box-background cursor-pointer hover:bg-gray-50 hover:dark:bg-gray-600 p-3 rounded-md relative shadow-md hover:shadow-lg"
              @click="editList(list)">
              <div class="flex flex-col flex-1 m-2 mb-16">
                <p class="text-light-black-text dark:text-dark-title-text text-lg truncate group-hover:text-dark-body-text">
                  {{ list.name }}
                </p>
                <p v-if="list.description" class="text-common-grey text-sm group-hover:text-dark-body-text">
                  {{ list.description }}
                </p>
                <p v-else class="text-common-grey text-sm group-hover:text-dark-body-text">
                  ({{ $t("no description available") }})
                </p>
              </div>

              <div class="mt-auto w-full flex justify-between divide-gray-200 overflow-visible absolute bottom-2">
                <button class="cancel-btn w-1/4 m-2 mt-3 mr-0" @click.stop="deleteList(list)">{{ $t("Delete") }}</button>
                <button class="primary-btn w-1/4 m-2 mt-3 mr-0 relative right-6" @click="editList(list)">{{ $t("Edit") }}</button>
              </div>
            </div>

            <div
              class="items-center dark:text-white justify-center h-36 col-span-1 bg-white dark:bg-dark-box-background cursor-pointer hover:bg-gray-50 hover:dark:bg-gray-600 p-3 rounded-md relative shadow-md hover:shadow-lg"
              @click="handleCreateNew">
              <span class="flex items-center justify-center h-full">
                <Plus class="w-7 h-7" />
                {{ $t("Add New") }}
              </span>
            </div>
          </div>
        </div>

        <div class="w-1/3 border dark:border-dark-divider-border dark:text-common-grey rounded-lg p-4 ml-6">
          <div class="text-xl mb-2">
            {{ $t("Explanation") }}
          </div>
          <p class="font-light">
            {{
              $t(
                "You can automate your lead generation, churn risk detection and more by creating Suggested lists based on a variety of criteria. RevSetter will automatically find customers or contacts that match your criteria and share that with the account owner."
              )
            }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="show_form" class="flex w-full gap-x-5 justify-between sm:overflow-hidden">
      <div class="relative -space-y-px w-2/3 mt-2">
        <NewSuggestedListForm
          v-if="!is_update_list"
          :metrics-list="selected_metrics"
          group-type="suggested"
          :object-type="current_object_type?.type"
          @cancel="
            () => {
              show_form = false
            }
          "
          @done="
            (new_list) => {
              suggested_lists.push(new_list)
              show_form = false
            }
          "
          @update="show_form = false" />
        <!-- TODO -->
        <UpdateSuggestedList
          v-else
          :metrics-list="selected_metrics"
          :object-type="current_object_type?.type"
          :suggested-list="selected_list"
          @cancel="
            () => {
              is_update_list = false
              show_form = false
            }
          "
          @done="
            (new_list) => {
              updateSuggestedList(new_list)
            }
          "
          @update="show_form = false" />
      </div>
      <div class="mt-14 w-1/3">
        <SelectInput
          :items="$store.state.object_types"
          :value="current_object_type"
          label-attr="label"
          :classes="`w-[21vw] mb-6`"
          :is-box-background="true"
          :label="$t('Object type')"
          :inline-label="true"
          :show-label="false"
          @update:currentItem="handleFilter($event)" />
        <div class="dark:text-white mb-2">
          {{ $t("Displayed Metrics") }}
        </div>

        <MultiSelectInputFlat
          :items="
            current_object_type?.type == 'company'
              ? [
                { name: $t('Actual value'), id: 's1' },
                { name: $t('Forecasted value'), id: 's2' }
              ].concat(
                $store.state.metrics.filter((t) => t.object_type == current_object_type?.type).filter((m) => m?.data_type === `Number`)
              )
              : $store.state.metrics.filter((t) => t.object_type == current_object_type?.type).filter((m) => m?.data_type === `Number`)
          "
          :value="selected_metrics"
          name="table_metrics"
          :is-draggable="true"
          label-attr="name"
          value-attr="id"
          :allow-null="false"
          :is-flat="true"
          :return-objects="false"
          :label="$t('Metrics list')"
          classes="w-full"
          input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
          :selection-text="null"
          :show-label="false"
          :limit="3"
          @update:currentItems="
            ($event) => {
              handleChange($event)
            }
          " />
      </div>
    </div>

    <Modal v-model:is-open="is_delete_list_modal_open" :default-success-msg="$t('Suggested List Deleted')">
      <template #form="{ close: closeModal, done: showIsDone, error: showError }">
        <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
          {{ $t("Delete Suggested List") }} {{ selected_list.name }}
        </h3>
        <div class="mt-2 max-w-xl text-sm text-gray-500">
          <p>{{ $t("Once you delete a suggested list, you can't restore it.") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 border border-transparent font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="removeSelectedList(showIsDone, showError)">
            {{ $t("Delete") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 border dark:border-dark-divider-border dark:text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
      <template #done="{ close: closeModal }">
        <div>
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <Check class="h-6 w-6 text-green-600" aria-hidden="true" />
          </div>
          <div class="mt-3 text-center sm:mt-5">
            <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              {{ $t("Suggested list deleted successfully") }}
            </h3>
          </div>
        </div>
        <div class="mt-5 sm:mt-6 text-center">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Done") }}</button>
        </div>
      </template>
      <template #error="{ close: closeModal }">
        <div class="flex flex-col items-center justify-center h-full px-4 text-center">
          <div class="flex justify-center">
            <CircleAlert class="w-20 h-20 m-10 text-red-400" aria-hidden="true" />
          </div>
          <h2 class="text-3xl font-extrabold text-gray-900 dark:text-dark-body-text">
            {{ $t("Error") }}
          </h2>
          <p class="mt-4 text-xl text-gray-500">
            {{ $t("Error deleting Suggested list, please try again!") }}
          </p>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import dataService from "@/services/data.service"
import Modal from "@/components/shared/Modal.vue"
import NewSuggestedListForm from "@/components/forms/NewSuggestedListForm.vue"
import UpdateSuggestedList from "@/components/forms/UpdateSuggestedList.vue"
import { Check, Plus, CircleAlert } from "lucide-vue-next"
import MultiSelectInputFlat from "@/components/shared/MultiSelectInputFlat.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { utils } from "@/store/helpers"

export default {
  components: {
    CircleAlert,
    NewSuggestedListForm,
    UpdateSuggestedList,
    Modal,
    Check,
    MultiSelectInputFlat,
    SelectInput,
    Plus
  },
  data() {
    return {
      suggested_lists: [],
      is_update_list: false,
      is_delete_list_modal_open: false,
      selected_list: null,
      selected_metrics: [],
      show_form: false,
      current_object_type: null
    }
  },
  created() {
    this.register_listeners()
    this.getSuggestedCompanies()
    if (this.$store.state.data.suggested_object_type) {
      this.current_object_type = this.$store.state.data.suggested_object_type
    } else {
      this.$store.dispatch(
        "data/setSuggestedObjectType",
        this.$store.state.object_types.find((s) => s.type === "company")
      )
      this.current_object_type = this.$store.state.object_types.find((s) => s.type === "company")
    }
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    updateSuggestedList(new_list) {
      this.$utils.replaceItem(this.suggested_lists, new_list)
      this.show_form = false
      this.$emitter.emit(ALL_EMITTERS.SUGGESTED_LIST_UPDATED_IN_PROSPECTING_COMPONENT, new_list)
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.REMOVE_SUGGESTED_LIST_IN_ADMIN_SETTINGS, (list_id) => {
        utils.removeItem(this.suggested_lists, list_id)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.REMOVE_SUGGESTED_LIST_IN_ADMIN_SETTINGS)
    },
    handleFilter(object_type) {
      this.current_object_type = object_type
      this.selected_metrics = []
      this.$store.dispatch("data/setSuggestedObjectType", object_type)
    },
    handleCreateNew() {
      this.show_form = true
      this.selected_metrics = []
    },
    handleChange(e) {
      this.selected_metrics = e.map((m) => {
        if (m === "s1") {
          return { name: this.$t("Actual value"), id: "s1" }
        } else if (m === "s2") {
          return { name: this.$t("Forecasted value"), id: "s2" }
        } else {
          return this.$utils.getMetricById(m)
        }
      })
    },
    getSuggestedCompanies() {
      this.suggested_lists = []
      this.$store.dispatch("ui/showLoading")
      dataService
        .getMatrixCompanies({
          type: "suggested",
          include_rollup_metrics: false,
          include_records: false
        })
        .then((result) => {
          this.$store.dispatch("ui/hideLoading")
          result.data.data.forEach((list) => {
            this.suggested_lists.push(list)
          })
        })
    },

    deleteList(list) {
      this.selected_list = list
      this.is_delete_list_modal_open = true
    },
    editList(list) {
      this.show_form = true
      this.selected_list = list
      this.selected_metrics = this.$store.state.data.solid_metrics_suggested[list.id]
        ? this.$store.state.data.solid_metrics_suggested[list.id].concat(
            list.displayed_metrics_stats.map((s) => this.$utils.getMetricById(s))
          )
        : [].concat(list.displayed_metrics_stats.map((s) => this.$utils.getMetricById(s)))
      this.is_update_list = true
    },
    removeSelectedList(showIsDone, showError) {
      dataService
        .deleteMatrix(this.selected_list.id)
        .then(() => {
          showIsDone()
          this.suggested_lists.map((list, index) => {
            if (list.id == this.selected_list.id) {
              this.suggested_lists.splice(index, 1)
              this.$emitter.emit(ALL_EMITTERS.REMOVE_SUGGESTED_LIST_IN_SUGGESTED_LIST_COMPONENT, this.selected_list.id)
              this.$emitter.emit(ALL_EMITTERS.REMOVE_SUGGESTED_LIST_IN_PROSPECTING_COMPONENT, this.selected_list.id)
              this.$emitter.emit(ALL_EMITTERS.REMOVE_SUGGESTED_LIST_IN_ADMIN_SETTINGS, this.selected_list.id)
            }
          })
          this.selected_list = null
        })
        .catch(() => {
          showError()
        })
    }
  }
}
</script>
